import {
  BUILT_IN_PPTX_THEME_NAMES,
  GridConfigSchema,
  ThemeConfigSchema
} from "./chunk-NKUYQDKA.js";
import {
  PptxComponentDefaultsSchema
} from "./chunk-Z6NUHS34.js";
import {
  PPTX_RENDERER_IDS,
  pptxPropsSchemaForRenderer
} from "./chunk-Y2ODB5VW.js";

// src/schemas/component-registry.ts
import { Type as Type5 } from "@sinclair/typebox";
import {
  PPTX_SLIDE_CONTENT_COMPONENTS,
  pptxComponentRequiresProps
} from "@json-to-office/shared/schemas/slide-content";
import { BlockInvocationPropsSchema } from "@json-to-office/shared";

// src/schemas/components/group.ts
import { Type } from "@sinclair/typebox";
import { GridPositionSchema } from "@json-to-office/shared/schemas/slide-content";
var Coord = (inches, percent) => Type.Union([
  Type.Number({ description: inches }),
  Type.String({ pattern: "^\\d+(\\.\\d+)?%$", description: percent })
]);
var PptxGroupPropsSchema = Type.Object(
  {
    x: Type.Optional(
      Coord(
        "Frame left edge in inches, relative to the enclosing frame or slide",
        "Frame left edge as a percentage of the enclosing width"
      )
    ),
    y: Type.Optional(
      Coord(
        "Frame top edge in inches, relative to the enclosing frame or slide",
        "Frame top edge as a percentage of the enclosing height"
      )
    ),
    w: Type.Optional(
      Coord(
        "Frame width in inches",
        "Frame width as a percentage of the enclosing width"
      )
    ),
    h: Type.Optional(
      Coord(
        "Frame height in inches",
        "Frame height as a percentage of the enclosing height"
      )
    ),
    grid: Type.Optional(GridPositionSchema),
    gridConfig: Type.Optional(
      Type.Object(
        { ...GridConfigSchema.properties },
        {
          additionalProperties: false,
          description: "Grid used for grid placements of this group\u2019s descendants, merged over the enclosing grid field by field. Inside a frame the grid spans the frame."
        }
      )
    ),
    direction: Type.Optional(
      Type.Union([Type.Literal("row"), Type.Literal("column")], {
        description: "Distribute the children into cells along this axis: equal cells, or weighted by `weights`. Each child fills its cell unless it states its own offsets; an omitted optional child simply redistributes the rest."
      })
    ),
    gap: Type.Optional(
      Type.Number({
        minimum: 0,
        description: "Space between distributed cells, in inches. Default 0."
      })
    ),
    weights: Type.Optional(
      Type.Array(Type.Number({ exclusiveMinimum: 0 }), {
        minItems: 1,
        description: "Relative cell sizes in child order (e.g. [1.1, 1] for a 1.1 : 1 split). Missing entries count as 1."
      })
    )
  },
  {
    additionalProperties: false,
    description: "Transparent group. Without a frame, a plain sequence of slide content; with `x`/`y`/`w`/`h` or `grid`, a frame its children position within (numbers are offsets in inches, percentages are fractions of the frame, omitted values fill it); with `direction`, a row or column that distributes its children into cells."
  }
);

// src/schemas/components/presentation.ts
import { Type as Type2 } from "@sinclair/typebox";
import {
  BlockDefinitionsSchema,
  FontRegistrySchema
} from "@json-to-office/shared";
var PresentationPropsSchema = Type2.Object(
  {
    title: Type2.Optional(
      Type2.String({ description: "Presentation title metadata" })
    ),
    author: Type2.Optional(
      Type2.String({ description: "Presentation author metadata" })
    ),
    subject: Type2.Optional(
      Type2.String({ description: "Presentation subject metadata" })
    ),
    company: Type2.Optional(
      Type2.String({ description: "Company name metadata" })
    ),
    theme: Type2.Optional(
      Type2.Union(
        [
          Type2.String({
            description: `Theme name to apply (default: "consulting"). Built-ins: ${BUILT_IN_PPTX_THEME_NAMES.join(", ")} (consulting is the house style).`,
            examples: [...BUILT_IN_PPTX_THEME_NAMES],
            default: "consulting"
          }),
          ThemeConfigSchema
        ],
        {
          description: 'Theme to apply: a built-in/custom theme name (default: "consulting"), or an inline theme config object so the document stays self-contained'
        }
      )
    ),
    fontRegistry: Type2.Optional(FontRegistrySchema),
    slideWidth: Type2.Optional(
      Type2.Number({
        description: "Slide width in inches (default: 10)",
        default: 10
      })
    ),
    slideHeight: Type2.Optional(
      Type2.Number({
        description: "Slide height in inches (default: 7.5)",
        default: 7.5
      })
    ),
    rtlMode: Type2.Optional(
      Type2.Boolean({ description: "Right-to-left text direction" })
    ),
    language: Type2.Optional(
      Type2.String({
        pattern: "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$",
        description: 'Default presentation language (BCP-47 tag, e.g. "en-US"). Sets the spell-check language for all text; individual text components can override it.',
        examples: ["en-US", "fr-FR", "de-DE", "it-IT", "es-ES"]
      })
    ),
    pageNumberFormat: Type2.Optional(
      Type2.Union([Type2.Literal("9"), Type2.Literal("09")], {
        description: 'Format for {PAGE_NUMBER} placeholders: "9" = bare number (default), "09" = zero-padded',
        default: "9"
      })
    ),
    componentDefaults: Type2.Optional(PptxComponentDefaultsSchema),
    grid: Type2.Optional(GridConfigSchema),
    blocks: Type2.Optional(BlockDefinitionsSchema),
    qualityProfile: Type2.Optional(
      Type2.String({
        description: "The quality profile validation judges this deck by when the caller names none \u2014 a blueprint scaffold writes its archetype\u2019s profile here. A profile names required structure and content; the theme only paints. Built-ins: consulting-deck, executive-presentation, technical-presentation; an unknown name falls back to the format default.",
        examples: [
          "consulting-deck",
          "executive-presentation",
          "technical-presentation"
        ]
      })
    )
  },
  {
    description: "Presentation container props",
    additionalProperties: false
  }
);

// src/schemas/components/slide.ts
import { Type as Type4 } from "@sinclair/typebox";

// src/schemas/components/common.ts
import { Type as Type3 } from "@sinclair/typebox";
import {
  ColorValueSchema,
  GradientFillSchema
} from "@json-to-office/shared/schemas/slide-content";
import {
  PptxAlignmentSchema,
  VerticalAlignmentSchema,
  ShadowSchema,
  GridPositionSchema as GridPositionSchema2
} from "@json-to-office/shared/schemas/slide-content";
var PositionSchema = Type3.Object(
  {
    x: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "X position in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: 'X position as percentage (e.g., "10%")'
        })
      ])
    ),
    y: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "Y position in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: 'Y position as percentage (e.g., "10%")'
        })
      ])
    ),
    w: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "Width in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: 'Width as percentage (e.g., "80%")'
        })
      ])
    ),
    h: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "Height in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: 'Height as percentage (e.g., "20%")'
        })
      ])
    )
  },
  {
    description: "Position and size in inches or percentages",
    additionalProperties: false
  }
);
var SlideBackgroundSchema = Type3.Object(
  {
    color: Type3.Optional(ColorValueSchema),
    gradient: Type3.Optional(GradientFillSchema),
    image: Type3.Optional(
      Type3.Object(
        {
          path: Type3.Optional(
            Type3.String({ description: "Image file path or URL" })
          ),
          base64: Type3.Optional(
            Type3.String({ description: "Base64-encoded image data" })
          )
        },
        { description: "Background image", additionalProperties: false }
      )
    )
  },
  {
    description: "Slide background configuration",
    additionalProperties: false
  }
);
var TransitionSchema = Type3.Object(
  {
    type: Type3.Optional(
      Type3.Union(
        [
          Type3.Literal("fade"),
          Type3.Literal("push"),
          Type3.Literal("wipe"),
          Type3.Literal("zoom"),
          Type3.Literal("none")
        ],
        { description: "Transition effect type" }
      )
    ),
    speed: Type3.Optional(
      Type3.Union(
        [Type3.Literal("slow"), Type3.Literal("medium"), Type3.Literal("fast")],
        { description: "Transition speed" }
      )
    )
  },
  {
    description: "Slide transition configuration",
    additionalProperties: false
  }
);

// src/schemas/components/slide.ts
var SlidePropsSchema = Type4.Object(
  {
    meta: Type4.Optional(
      Type4.Object(
        {
          title: Type4.Optional(
            Type4.String({
              description: "Authoring label for this slide, shown in editors and outlines. Never rendered \u2014 slide content is unaffected."
            })
          )
        },
        {
          additionalProperties: false,
          description: "Authoring metadata; has no effect on the presentation."
        }
      )
    ),
    background: Type4.Optional(SlideBackgroundSchema),
    transition: Type4.Optional(TransitionSchema),
    notes: Type4.Optional(
      Type4.String({ description: "Speaker notes for this slide" })
    ),
    hidden: Type4.Optional(
      Type4.Boolean({ description: "Hide this slide from presentation" })
    )
  },
  {
    description: "Slide container props",
    additionalProperties: false
  }
);

// src/schemas/component-registry.ts
var SLIDE_CHILDREN = [
  ...PPTX_SLIDE_CONTENT_COMPONENTS.map(({ name }) => name),
  "block",
  "group"
];
var PPTX_STANDARD_COMPONENTS_REGISTRY = [
  // ========================================================================
  // Container Components (can contain children)
  // ========================================================================
  {
    name: "pptx",
    propsSchema: PresentationPropsSchema,
    hasChildren: true,
    allowedChildren: ["slide"],
    category: "container",
    // Every presentation prop is optional, but the root is where a deck
    // states its title, size and theme, and both the published schema and
    // the deep validator have always demanded the key. Keeping it required
    // is a decision, not the schema's own answer, so it is declared.
    propsRequired: true,
    description: "Main presentation container - defines the overall presentation structure. Required as the root component. Always declare `slideWidth`/`slideHeight` (13.333 \xD7 7.5 for 16:9) \u2014 omitted, the renderer silently falls back to 4:3 and 16:9 content leaves a dead strip.",
    special: {
      hasSchemaField: true
    }
  },
  {
    name: "slide",
    propsSchema: SlidePropsSchema,
    hasChildren: true,
    allowedChildren: SLIDE_CHILDREN,
    category: "container",
    // No `propsRequired`: every slide prop is optional and nothing outside
    // the schema asks for one, so `{ "name": "slide", "children": [...] }`
    // is a whole slide and the published schema says so.
    description: "Slide container - groups content elements on a single slide. One idea per slide: past roughly 40 words of body text, split the content across more slides."
  },
  {
    name: "group",
    propsSchema: PptxGroupPropsSchema,
    hasChildren: true,
    allowedChildren: SLIDE_CHILDREN,
    category: "layout",
    description: "Transparent group of slide content; also the inspectable result of block expansion. Give it a frame (x/y/w/h or grid) to position children within it, and a direction to distribute them into equal or weighted cells."
  },
  {
    name: "block",
    propsSchema: BlockInvocationPropsSchema,
    hasChildren: false,
    category: "layout",
    description: "Invoke a JSON block defined in this document\u2019s props.blocks. Fill its named slots; no block names are built into the engine and no coordinates are accepted here."
  },
  // Content components are canonical in @json-to-office/shared so DOCX
  // visuals can reuse the exact schemas without depending on shared-pptx.
  ...PPTX_SLIDE_CONTENT_COMPONENTS
];
function getPptxStandardComponent(name) {
  return PPTX_STANDARD_COMPONENTS_REGISTRY.find((c) => c.name === name);
}
function getAllPptxComponentNames() {
  return PPTX_STANDARD_COMPONENTS_REGISTRY.map((c) => c.name);
}
function getPptxComponentsByCategory(category) {
  return PPTX_STANDARD_COMPONENTS_REGISTRY.filter(
    (c) => c.category === category
  );
}
function getPptxContainerComponents() {
  return PPTX_STANDARD_COMPONENTS_REGISTRY.filter((c) => c.hasChildren);
}
function getPptxContentComponents() {
  return PPTX_STANDARD_COMPONENTS_REGISTRY.filter((c) => !c.hasChildren);
}
function isPptxStandardComponent(name) {
  return PPTX_STANDARD_COMPONENTS_REGISTRY.some((c) => c.name === name);
}
function createPptxComponentSchemaObject(component, recursiveRef, profile) {
  const schema = {
    name: Type5.Literal(component.name),
    id: Type5.Optional(Type5.String()),
    enabled: Type5.Optional(
      Type5.Boolean({
        default: true,
        description: "When false, this component is filtered out and not rendered. Defaults to true."
      })
    )
  };
  if (component.special?.hasSchemaField) {
    schema.$schema = Type5.Optional(Type5.String({ format: "uri" }));
    schema.renderer = profile ? profile.requireDiscriminator ? Type5.Literal(profile.renderer, {
      description: "Renderer backend for this presentation"
    }) : Type5.Optional(
      Type5.Literal(profile.renderer, {
        description: 'Renderer backend. Omitted defaults to "pptxgenjs".'
      })
    ) : Type5.Optional(
      Type5.Union(
        PPTX_RENDERER_IDS.map((renderer) => Type5.Literal(renderer)),
        {
          description: 'Renderer backend. Omitted defaults to "pptxgenjs".'
        }
      )
    );
  }
  schema.props = profile ? pptxPropsSchemaForRenderer(
    component.name,
    component.propsSchema,
    profile.renderer
  ) : component.propsSchema;
  if (component.hasChildren && recursiveRef) {
    schema.children = Type5.Optional(Type5.Array(recursiveRef));
  }
  if (!pptxComponentRequiresProps(component)) {
    schema.props = Type5.Optional(schema.props);
  }
  return Type5.Object(schema, {
    additionalProperties: false,
    description: component.description
  });
}
function createAllPptxComponentSchemas(recursiveRef) {
  return PPTX_STANDARD_COMPONENTS_REGISTRY.map(
    (component) => createPptxComponentSchemaObject(component, recursiveRef)
  );
}
function createAllPptxComponentSchemasNarrowed(selfRef, pluginSchemas = [], profile) {
  const leafSchemas = /* @__PURE__ */ new Map();
  for (const comp of PPTX_STANDARD_COMPONENTS_REGISTRY) {
    if (!comp.hasChildren) {
      leafSchemas.set(
        comp.name,
        createPptxComponentSchemaObject(comp, void 0, profile)
      );
    }
  }
  const containers = PPTX_STANDARD_COMPONENTS_REGISTRY.filter(
    (c) => c.hasChildren
  );
  const resolved = /* @__PURE__ */ new Map();
  const pending = [...containers];
  while (pending.length > 0) {
    const before = pending.length;
    for (let i = pending.length - 1; i >= 0; i--) {
      const comp = pending[i];
      if (comp.name === "group") {
        const content = Type5.Intersect([
          selfRef,
          Type5.Object({
            name: Type5.Union([
              ...comp.allowedChildren.map((name) => Type5.Literal(name)),
              ...pluginSchemas.map(
                (schema) => schema.properties?.name ?? Type5.String()
              )
            ])
          })
        ]);
        resolved.set(
          comp.name,
          createPptxComponentSchemaObject(comp, content, profile)
        );
        pending.splice(i, 1);
        continue;
      }
      if (!comp.allowedChildren) {
        resolved.set(
          comp.name,
          createPptxComponentSchemaObject(comp, selfRef, profile)
        );
        pending.splice(i, 1);
        continue;
      }
      const containerDeps = comp.allowedChildren.filter(
        (name) => containers.some((c) => c.name === name)
      );
      if (!containerDeps.every((d) => resolved.has(d))) continue;
      const childSchemas = comp.allowedChildren.map((name) => resolved.get(name) ?? leafSchemas.get(name)).filter((s) => s !== void 0);
      const allChildSchemas = [...childSchemas, ...pluginSchemas];
      const childrenType = allChildSchemas.length === 1 ? allChildSchemas[0] : Type5.Union(allChildSchemas);
      resolved.set(
        comp.name,
        createPptxComponentSchemaObject(comp, childrenType, profile)
      );
      pending.splice(i, 1);
    }
    if (pending.length === before) {
      throw new Error(
        `Circular allowedChildren among: ${pending.map((c) => c.name).join(", ")}`
      );
    }
  }
  return [...resolved.values(), ...leafSchemas.values()];
}

export {
  PositionSchema,
  SlideBackgroundSchema,
  TransitionSchema,
  PptxAlignmentSchema,
  VerticalAlignmentSchema,
  ShadowSchema,
  GridPositionSchema2 as GridPositionSchema,
  PresentationPropsSchema,
  SlidePropsSchema,
  PptxGroupPropsSchema,
  pptxComponentRequiresProps,
  PPTX_STANDARD_COMPONENTS_REGISTRY,
  getPptxStandardComponent,
  getAllPptxComponentNames,
  getPptxComponentsByCategory,
  getPptxContainerComponents,
  getPptxContentComponents,
  isPptxStandardComponent,
  createPptxComponentSchemaObject,
  createAllPptxComponentSchemas,
  createAllPptxComponentSchemasNarrowed
};
//# sourceMappingURL=chunk-VN3IXWBP.js.map